import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronLeft, ChevronRight, Phone, ShoppingCart, Check, ArrowRight, Info, Calculator, CheckCircle2, RefreshCw, X, Minus, Plus, ZoomIn } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from './CartContext';
import { categories } from '../data/products';
import { QuoteModal } from './QuoteModal';
const TOP_SELLER_SLUGS = new Set(['5-8-minus', '1-25-minus', '5-8-clear', '1-5-drain-rock', '2-4-quarry-rock', '3-way-topsoil', 'medium-bark']);
function getDensity(productName: string): number {
  const name = productName.toLowerCase();
  if (name.includes('bark') || name.includes('play chips')) return 0.45;
  if (name.includes('compost')) return 0.6;
  if (name.includes('sand')) return 1.4;
  return 1.3;
}
function isLightMaterial(name: string): boolean {
  const n = name.toLowerCase();
  return n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('50/50') || n.includes('fill dirt') || n.includes('play chips');
}
const ProductImageCarousel = ({
  images,
  alt
}: {
  images: string[];
  alt: string;
}) => {
  const [idx, setIdx] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [is3DMode, setIs3DMode] = useState(false);
  const [mousePos, setMousePos] = useState({
    x: 0,
    y: 0
  });
  useEffect(() => {
    setIdx(0);
    setIsPaused(false);
    setIs3DMode(false);
  }, [images]);
  // Auto-advance every 5 seconds
  useEffect(() => {
    if (!images || images.length <= 1 || isPaused || isZoomed || is3DMode) return;
    const timer = setInterval(() => {
      setIdx(prev => prev === images.length - 1 ? 0 : prev + 1);
    }, 5000);
    return () => clearInterval(timer);
  }, [images, isPaused, isZoomed, is3DMode]);
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!is3DMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setMousePos({
      x,
      y
    });
  };
  const handleMouseLeave = () => {
    setIsPaused(false);
    if (is3DMode) {
      setMousePos({
        x: 0,
        y: 0
      });
    }
  };
  const activeImage = images && images.length > 0 ? images[idx] : '';
  return <>
      <div className="absolute inset-0 w-full h-full cursor-pointer group overflow-hidden" onClick={() => !is3DMode && setIsZoomed(true)} onMouseEnter={() => setIsPaused(true)} onMouseLeave={handleMouseLeave} onMouseMove={handleMouseMove} data-id="element-883">
        {is3DMode ? <div className="absolute inset-0 bg-stone-900 flex items-center justify-center overflow-hidden" style={{
        perspective: '1000px'
      }} data-id="element-884">
            <motion.div animate={{
          rotateX: mousePos.y * -40,
          rotateY: mousePos.x * 40
        }} transition={{
          type: 'spring',
          stiffness: 150,
          damping: 20
        }} className="w-full h-full relative flex items-center justify-center" style={{
          transformStyle: 'preserve-3d'
        }} data-id="element-885">
              {/* Base shadow */}
              <div className="absolute w-3/4 h-3/4 bg-black/80 rounded-full blur-2xl" style={{
            transform: 'translateZ(-60px) scale(0.9)'
          }} data-id="element-886" />

              {/* Layer 1 (Back) */}
              <img src={activeImage} alt={alt} className="absolute w-3/4 h-3/4 object-cover rounded-full opacity-40 mix-blend-screen" style={{
            transform: 'translateZ(-30px) scale(1.1)'
          }} data-id="element-887" />

              {/* Layer 2 (Middle) */}
              <img src={activeImage} alt={alt} className="absolute w-3/4 h-3/4 object-cover rounded-full opacity-70" style={{
            transform: 'translateZ(10px) scale(1)'
          }} data-id="element-888" />

              {/* Layer 3 (Front) */}
              <img src={activeImage} alt={alt} className="absolute w-3/4 h-3/4 object-cover rounded-full shadow-2xl border-4 border-stone-800/50" style={{
            transform: 'translateZ(50px) scale(0.9)'
          }} data-id="element-889" />
            </motion.div>
            <div className="absolute bottom-4 left-0 right-0 text-center text-white/50 text-xs font-medium tracking-widest uppercase pointer-events-none" data-id="element-890">
              Interactive 3D Preview (Move Mouse)
            </div>
          </div> : !images || images.length <= 1 ? <img src={images[0]} alt={alt} className="absolute inset-0 w-full h-full object-cover" data-id="element-891" /> : images.map((src, i) => <img key={src} src={src} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500" style={{
        opacity: i === idx ? 1 : 0
      }} data-id="element-892" />)}

        {/* 3D Toggle Button */}
        <button onClick={e => {
        e.stopPropagation();
        setIs3DMode(!is3DMode);
      }} className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 backdrop-blur-md text-white p-2 rounded-full transition-colors z-10" title={is3DMode ? 'Exit 3D View' : 'View in 3D'} data-id="element-893">
          <RefreshCw className={`w-4 h-4 ${is3DMode ? 'text-brand-orange' : ''}`} data-id="element-894" />
        </button>
      </div>

      {/* Fullscreen Zoom Overlay */}
      <AnimatePresence data-id="element-895">
        {isZoomed && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center touch-none" onClick={() => setIsZoomed(false)} data-id="element-896">
            <button className="absolute top-6 right-6 text-white/70 hover:text-white bg-black/50 p-2 rounded-full transition-colors z-[110]" onClick={e => {
          e.stopPropagation();
          setIsZoomed(false);
        }} aria-label="Close zoom" data-id="element-897">
              <X className="w-6 h-6" data-id="element-898" />
            </button>

            <motion.img initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} src={activeImage} alt={alt} className="max-w-full max-h-full object-contain" style={{
          touchAction: 'pan-x pan-y pinch-zoom'
        }} data-id="element-899" />
          </motion.div>}
      </AnimatePresence>
    </>;
};
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-900">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full px-4 py-3 bg-brand-lightgray/30 dark:bg-[#383838] border border-stone-200 dark:border-[#4b4b4b] rounded-none text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all flex items-center justify-between font-medium ${value ? '' : 'text-stone-400'} bg-[#353535] bg-[#303030]`} data-id="element-901">
        <span className="truncate" data-id="element-902">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-903" />
      </button>

      <AnimatePresence data-id="element-904">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full bottom-full mb-2 bg-white dark:bg-[#383838] border border-stone-200 dark:border-[#4b4b4b] rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-id="element-905">
            {options.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
          if (!opt.disabled) {
            onChange(opt.value);
            setIsOpen(false);
          }
        }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${opt.disabled ? 'text-stone-300 dark:text-white/30 cursor-not-allowed bg-stone-50 dark:bg-stone-900/50' : value === opt.value ? 'bg-brand-orange/20 text-brand-orange' : 'text-brand-charcoal dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-906">
                {opt.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const projectTypes = [{
  id: 'driveway',
  name: 'Driveway',
  defaultDepth: 4
}, {
  id: 'patio',
  name: 'Patio Base',
  defaultDepth: 4
}, {
  id: 'walkway',
  name: 'Walkway',
  defaultDepth: 3
}, {
  id: 'parking',
  name: 'Parking Pad',
  defaultDepth: 6
}, {
  id: 'french-drain',
  name: 'French Drain',
  defaultDepth: 12
}, {
  id: 'fill',
  name: 'General Fill',
  defaultDepth: 6
}];
const calcMaterials = [
// Base & Compaction
{
  id: '125-minus',
  name: '1.25" Minus',
  density: 1.4,
  priceRange: '$55'
}, {
  id: 'crushed-rock',
  name: '5/8" Minus',
  density: 1.4,
  priceRange: '$55'
}, {
  id: 'walkway-gravel',
  name: 'Walkway Gravel (3/8" Minus)',
  density: 1.4,
  priceRange: '$50'
}, {
  id: 'recycled-asphalt',
  name: 'Recycled Asphalt',
  density: 1.4,
  priceRange: '$38'
}, {
  id: 'recycled-concrete',
  name: 'Recycled Concrete',
  density: 1.4,
  priceRange: '$38'
}, {
  id: 'builders-sand',
  name: 'Builders Sand',
  density: 1.5,
  priceRange: '$58'
}, {
  id: 'type-17',
  name: 'Gravel Borrow',
  density: 1.4,
  priceRange: '$38'
},
// Clean Drain Rock
{
  id: '58-clear',
  name: '5/8" Clean',
  density: 1.35,
  priceRange: '$70'
}, {
  id: '78-drain',
  name: '7/8" Drain Rock',
  density: 1.35,
  priceRange: '$62'
}, {
  id: '125-clear',
  name: '1.25" Clean',
  density: 1.35,
  priceRange: '$62'
}, {
  id: '15-drain',
  name: '1.5" Drain Rock',
  density: 1.35,
  priceRange: '$69'
}, {
  id: 'pea-gravel',
  name: 'Pea Gravel',
  density: 1.45,
  priceRange: '$54'
},
// Soil, Mulch & Organic
{
  id: 'topsoil',
  name: '3-Way Topsoil',
  density: 1.1,
  priceRange: '$43'
}, {
  id: '5050-mix',
  name: '50/50 Mix',
  density: 1.1,
  priceRange: '$51'
}, {
  id: 'fill-dirt',
  name: 'Fill Dirt',
  density: 1.3,
  priceRange: '$20'
}, {
  id: 'compost',
  name: 'Compost',
  density: 0.6,
  priceRange: '$60'
}, {
  id: 'fine-bark',
  name: 'Fine Bark',
  density: 0.45,
  priceRange: '$48'
}, {
  id: 'medium-bark',
  name: 'Medium Bark',
  density: 0.45,
  priceRange: '$48'
}, {
  id: 'coarse-bark',
  name: 'Coarse Bark',
  density: 0.45,
  priceRange: '$48'
}, {
  id: 'play-chips',
  name: 'Play Chips',
  density: 0.45,
  priceRange: '$50'
},
// Landscape & Decor
{
  id: 'streambed-cobble',
  name: 'Streambed Cobble',
  density: 1.5,
  priceRange: '$55'
}, {
  id: 'river-rock',
  name: 'River Rock',
  density: 1.5,
  priceRange: '$76'
}, {
  id: 'lava-rock',
  name: 'Lava Rock',
  density: 0.75,
  priceRange: '$84'
},
// Quarry Rock
{
  id: 'quarry-rock',
  name: '2-4" Spalls',
  density: 1.5,
  priceRange: '$70'
}, {
  id: '48-quarry',
  name: '4-8" Spalls',
  density: 1.5,
  priceRange: '$70'
}, {
  id: '2-ballast',
  name: '2" Ballast',
  density: 1.5,
  priceRange: '$55'
}, {
  id: 'large-rocks',
  name: 'Large Rocks (1-4 Man)',
  density: 1.6,
  priceRange: '$125'
}];
interface QuizOption {
  value: string;
  label: string;
  result?: {
    name: string;
    desc: string;
  };
}
interface QuizQuestion {
  id: string;
  question: string;
  options: QuizOption[];
  dependsOn?: {
    questionId: string;
    value: string;
  };
}
const quizQuestions: QuizQuestion[] = [{
  id: 'project',
  question: 'What type of project are you working on?',
  options: [{
    value: 'driveway',
    label: 'Driveway or Parking Area'
  }, {
    value: 'drainage',
    label: 'Drainage (French drain, retaining wall)'
  }, {
    value: 'landscape',
    label: 'Landscaping or Garden Beds'
  }, {
    value: 'walkway',
    label: 'Walkway or Patio Base'
  }, {
    value: 'erosion',
    label: 'Erosion Control or Slope Stabilization'
  }, {
    value: 'concrete',
    label: 'Concrete, Masonry, or Leveling'
  }, {
    value: 'fill',
    label: 'Grading, Fill, or Raising Low Spots'
  }]
}, {
  id: 'driveway_layer',
  dependsOn: {
    questionId: 'project',
    value: 'driveway'
  },
  question: 'Which layer are you building?',
  options: [{
    label: 'The deep foundation (Base layer)',
    value: 'base',
    result: {
      name: '1.25" Minus',
      desc: 'A coarser crushed rock that compacts into a rock-solid foundation. Often used as the first layer before topping with finer gravel.'
    }
  }, {
    label: 'The top driving surface (Top layer)',
    value: 'top',
    result: {
      name: '5/8" Minus',
      desc: 'Our most popular driveway gravel — packs down tight and creates a smooth, firm driving surface.'
    }
  }, {
    label: 'Budget-friendly option (Recycled material)',
    value: 'budget',
    result: {
      name: 'Recycled Concrete or Recycled Asphalt',
      desc: 'Sustainable, affordable alternatives that perform like virgin rock. Recycled Asphalt even re-binds over time for a semi-hard surface. Great for rural driveways and parking areas.'
    }
  }]
}, {
  id: 'drainage_type',
  dependsOn: {
    questionId: 'project',
    value: 'drainage'
  },
  question: 'What kind of drainage?',
  options: [{
    label: 'Standard French Drain',
    value: 'french',
    result: {
      name: '7/8" Drain Rock',
      desc: 'The Puget Sound standard for residential French drains. Washed clean so water flows freely through it.'
    }
  }, {
    label: 'Heavy-duty / Behind Retaining Wall',
    value: 'heavy',
    result: {
      name: '1.5" Drain Rock or 1.25" Clean',
      desc: 'Larger stone with bigger voids for maximum water flow in heavy-duty drainage and retaining wall backfill.'
    }
  }]
}, {
  id: 'landscape_type',
  dependsOn: {
    questionId: 'project',
    value: 'landscape'
  },
  question: 'What are you trying to achieve?',
  options: [{
    label: 'Planting grass or new plants',
    value: 'plants',
    result: {
      name: '3-Way Topsoil',
      desc: 'A premium blend of topsoil, compost, and sand — everything your plants need to thrive from day one.'
    }
  }, {
    label: 'Amending heavy clay soil',
    value: 'amend',
    result: {
      name: '50/50 Mix',
      desc: 'Half topsoil, half compost. Packed with organic matter to break up clay and boost soil health.'
    }
  }, {
    label: 'Pure compost for enriching soil',
    value: 'compost',
    result: {
      name: 'Compost',
      desc: 'Rich, fully aged organic compost packed with beneficial microbes and nutrients. Top-dress lawns, enrich raised beds, or boost vegetable garden health.'
    }
  }, {
    label: 'Weed control and moisture retention',
    value: 'weeds',
    result: {
      name: 'Fine, Medium, or Coarse Bark',
      desc: 'Natural PNW bark mulch to protect beds, suppress weeds, and retain moisture. Fine Bark for a polished look, Medium for all-around use, Coarse for maximum longevity.'
    }
  }, {
    label: 'Decorative ground cover or dry creek bed',
    value: 'decorative',
    result: {
      name: 'Streambed Cobble, River Rock, or Lava Rock',
      desc: 'Streambed Cobble for a natural creek bed look, River Rock for smooth organic accents, or Lava Rock for bold reddish-brown color and lightweight coverage. All zero-maintenance.'
    }
  }, {
    label: 'Decorative walking path',
    value: 'path',
    result: {
      name: 'Pea Gravel',
      desc: 'Small, smooth rounded stones for a beautiful, crunching pathway that drains well.'
    }
  }, {
    label: 'Playground or play area surface',
    value: 'playground',
    result: {
      name: 'Play Chips',
      desc: 'Natural cedar wood chips screened for playground use. Soft, cushioning, and naturally insect-resistant. Meets safety standards for fall protection when installed at proper depth.'
    }
  }]
}, {
  id: 'walkway_type',
  dependsOn: {
    questionId: 'project',
    value: 'walkway'
  },
  question: 'What is the surface going to be?',
  options: [{
    label: 'Pavers or Flagstone (Need a base)',
    value: 'pavers',
    result: {
      name: '5/8" Minus + Builders Sand',
      desc: 'Use 5/8" Minus as a compacted sub-base, then a layer of Builders Sand to level and set your pavers.'
    }
  }, {
    label: 'Just gravel (Walking on it directly)',
    value: 'gravel',
    result: {
      name: 'Pea Gravel or 5/8" Minus',
      desc: 'Pea gravel for a loose decorative feel, or 5/8" Minus for a firm, packed path.'
    }
  }]
}, {
  id: 'erosion_type',
  dependsOn: {
    questionId: 'project',
    value: 'erosion'
  },
  question: 'What does the area look like?',
  options: [{
    label: 'Steep slope or embankment',
    value: 'steep',
    result: {
      name: '2-4" Quarry Spalls or Large Rocks',
      desc: 'Quarry Spalls (2-4") lock in place on slopes for standard rip-rap. For extreme erosion or shoreline protection, Large Rocks (1-4 man) provide maximum mass and stability — nothing moves them.'
    }
  }, {
    label: 'Gentle slope or ditch lining',
    value: 'gentle',
    result: {
      name: '1.25" Clean, Streambed Cobble, or 2-4" Quarry Spalls',
      desc: 'For gentle slopes, 1.25" Clean provides drainage and stability. Streambed Cobble offers a natural, decorative look while controlling erosion. For steeper ditches, step up to 2-4" Quarry Spalls.'
    }
  }, {
    label: 'Severe erosion or shoreline armoring',
    value: 'severe',
    result: {
      name: 'Large Rocks (1-4 Man)',
      desc: 'Massive quarry rocks ranging from 100 lbs to 2,000+ lbs. The ultimate solution for shoreline protection, steep bank armoring, and heavy rip-rap where nothing else will hold.'
    }
  }]
}, {
  id: 'concrete_type',
  dependsOn: {
    questionId: 'project',
    value: 'concrete'
  },
  question: 'What are you doing?',
  options: [{
    label: 'Mixing concrete or mortar',
    value: 'mixing',
    result: {
      name: 'Builders Sand',
      desc: 'Coarse, washed sand with consistent grain size — essential for mixing concrete, mortar, and stucco.'
    }
  }, {
    label: 'Leveling under pavers, a pool, or a shed',
    value: 'leveling',
    result: {
      name: 'Builders Sand',
      desc: 'Clean, coarse sand that spreads and levels easily. Perfect as a setting bed under pavers, pools, and structures.'
    }
  }]
}, {
  id: 'fill_type',
  dependsOn: {
    questionId: 'project',
    value: 'fill'
  },
  question: 'What do you need to accomplish?',
  options: [{
    label: "Raise grade or fill low spots (won't be planting)",
    value: 'grade',
    result: {
      name: 'Fill Dirt',
      desc: 'Our most affordable bulk material. Clean subsoil for raising grades, backfilling, and establishing drainage slopes.'
    }
  }, {
    label: 'Fill and then plant on top',
    value: 'plant',
    result: {
      name: 'Fill Dirt + 3-Way Topsoil',
      desc: 'Use Fill Dirt to build up the area, then top with 4-6 inches of 3-Way Topsoil for a plantable surface.'
    }
  }, {
    label: 'Affordable bulk fill for subgrade or earthwork',
    value: 'bulk',
    result: {
      name: 'Gravel Borrow',
      desc: 'A naturally occurring pit-run gravel blend that compacts well. Meets WSDOT Type 17 specs — great for building up subgrade, backfill, and general earthwork at a lower cost than crushed rock.'
    }
  }]
}];
export function Services() {
  const [activeTab, setActiveTab] = useState<'materials' | 'calculator' | 'quiz'>('materials');
  const {
    addToCart,
    openCart
  } = useCart();
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  // Add to Cart State
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const [selectedQuantity, setSelectedQuantity] = useState<number>(12);
  const [showQuantityDropdown, setShowQuantityDropdown] = useState(false);
  const [addedFlash, setAddedFlash] = useState<string | null>(null);
  const [showAllCatalog, setShowAllCatalog] = useState(false);
  const [categoryGridView, setCategoryGridView] = useState<string | null>(null);
  const [categoryOverview, setCategoryOverview] = useState<string | null>(null);
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [swipeHintDismissed, setSwipeHintDismissed] = useState(false);
  // Inline Calculator State
  const [inlineCalcLength, setInlineCalcLength] = useState('');
  const [inlineCalcWidth, setInlineCalcWidth] = useState('');
  const [inlineCalcDepth, setInlineCalcDepth] = useState('3');
  const [showInlineCalc, setShowInlineCalc] = useState(false);
  // Swipe hint: show after 60s of browsing products in detail view
  useEffect(() => {
    if (expandedCategory && !categoryOverview && !swipeHintDismissed) {
      const timer = setTimeout(() => {
        setShowSwipeHint(true);
        // Auto-dismiss after 5 seconds
        setTimeout(() => setShowSwipeHint(false), 5000);
      }, 60000);
      return () => clearTimeout(timer);
    } else {
      setShowSwipeHint(false);
    }
  }, [expandedCategory, categoryOverview, swipeHintDismissed]);
  const handleAddToCartClick = (productName: string, categoryName: string) => {
    setAddingToCart(productName);
    setSelectedQuantity(12);
  };
  const handleConfirmAdd = (categoryName: string, productName: string, price: number) => {
    const isLight = isLightMaterial(productName);
    const fullLoad = isLight ? 15 : 12;
    const maxQuantity = isLight ? 60 : 48;
    if (selectedQuantity > maxQuantity) {
      setIsQuoteModalOpen(true);
      return;
    }
    if (selectedQuantity > fullLoad) {
      // Split into multiple deliveries of fullLoad each + remainder
      let remaining = selectedQuantity;
      let deliveryNum = 1;
      while (remaining > 0) {
        const qty = Math.min(remaining, fullLoad);
        const label = selectedQuantity > fullLoad ? `${productName} (Delivery ${deliveryNum})` : productName;
        addToCart({
          category: categoryName,
          product: label,
          quantity: qty,
          pricePerYard: price
        });
        remaining -= qty;
        deliveryNum++;
      }
    } else {
      addToCart({
        category: categoryName,
        product: productName,
        quantity: selectedQuantity,
        pricePerYard: price
      });
    }
    setAddingToCart(null);
    setAddedFlash(productName);
  };
  // Calculator State
  const [projectType, setProjectType] = useState(projectTypes[0].id);
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [depth, setDepth] = useState(projectTypes[0].defaultDepth.toString());
  const [material, setMaterial] = useState(calcMaterials[0].id);
  const [calcResults, setCalcResults] = useState<{
    yards: number;
    tons: number;
  } | null>(null);
  useEffect(() => {
    const selectedProject = projectTypes.find(p => p.id === projectType);
    if (selectedProject) {
      setDepth(selectedProject.defaultDepth.toString());
    }
  }, [projectType]);
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const l = parseFloat(length);
    const w = parseFloat(width);
    const d = parseFloat(depth);
    if (isNaN(l) || isNaN(w) || isNaN(d)) return;
    const depthInFeet = d / 12;
    const cubicFeet = l * w * depthInFeet;
    const cubicYards = cubicFeet / 27;
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    const density = selectedMaterial ? selectedMaterial.density : 1.4;
    const tons = cubicYards * density;
    setCalcResults({
      yards: Math.ceil(cubicYards * 10) / 10,
      tons: Math.ceil(tons * 10) / 10
    });
  };
  const handleCalcAddToCart = () => {
    if (!calcResults) return;
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    if (!selectedMaterial) return;
    // Find matching product in categories
    let matchedProduct: any = null;
    let matchedCategory: string = '';
    for (const cat of categories) {
      const prod = cat.products.find(p => p.name === selectedMaterial.name);
      if (prod) {
        matchedProduct = prod;
        matchedCategory = cat.name;
        break;
      }
    }
    if (matchedProduct) {
      const roundedYards = Math.ceil(calcResults.yards);
      addToCart({
        category: matchedCategory,
        product: matchedProduct.name,
        quantity: roundedYards,
        pricePerYard: matchedProduct.pricePerYard
      });
      setAddedFlash(matchedProduct.name);
      setTimeout(() => setAddedFlash(null), 2000);
      openCart();
    } else {
      setIsQuoteModalOpen(true);
    }
  };
  // Quiz State
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [quizResult, setQuizResult] = useState<{
    name: string;
    desc: string;
  } | null>(null);
  const currentQuizQuestion = quizQuestions.find(q => {
    if (!q.dependsOn) return !quizAnswers[q.id];
    return quizAnswers[q.dependsOn.questionId] === q.dependsOn.value && !quizAnswers[q.id];
  });
  const handleQuizAnswer = (questionId: string, value: string, result?: {
    name: string;
    desc: string;
  }) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    if (result) {
      setQuizResult(result);
    }
  };
  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizResult(null);
  };
  const tabs = [{
    id: 'materials' as const,
    label: 'Our Materials'
  }, {
    id: 'calculator' as const,
    label: 'Calculator'
  }, {
    id: 'quiz' as const,
    label: 'Help Me Choose'
  }];
  // Carousel state
  const [carouselIndex, setCarouselIndex] = useState(0);
  // Reset quantity when carousel index or expanded category changes
  useEffect(() => {
    if (expandedCategory) {
      const category = categories.find(c => c.name === expandedCategory);
      if (category) {
        const product = category.products[carouselIndex];
        if (product) {
          setSelectedQuantity(12);
          setAddingToCart(null);
          setShowInlineCalc(false);
          setInlineCalcLength('');
          setInlineCalcWidth('');
        }
      }
    }
  }, [carouselIndex, expandedCategory]);
  // Handle navigation from Navbar shop dropdown
  useEffect(() => {
    const handleMaterialsNav = () => {
      const params = new URLSearchParams(window.location.search);
      const cat = params.get('cat');
      const prod = params.get('prod');
      if (cat) {
        setExpandedCategory(cat);
        setCarouselIndex(prod ? parseInt(prod, 10) : 0);
        setActiveTab('materials');
        // Clear query params so a refresh doesn't re-open the card
        const cleanUrl = window.location.pathname + window.location.hash.split('?')[0];
        window.history.replaceState({}, '', cleanUrl);
        const scrollToCategory = () => {
          const catEl = document.getElementById(`cat-${cat}`);
          const target = catEl || document.getElementById('materials');
          if (target) {
            const navHeight = document.querySelector('nav')?.offsetHeight || 80;
            const top = target.getBoundingClientRect().top + window.scrollY - navHeight + 10;
            window.scrollTo({
              top,
              behavior: 'smooth'
            });
          }
          // Scroll selected product tab to the left edge of the carousel
          const tabContainer = catEl?.querySelector('.overflow-x-auto');
          if (tabContainer) {
            const prodIdx = prod ? parseInt(prod, 10) : 0;
            const buttons = tabContainer.querySelectorAll('button');
            if (buttons[prodIdx]) {
              const btn = buttons[prodIdx] as HTMLElement;
              const scrollOffset = btn.offsetLeft - 4;
              tabContainer.scrollTo({
                left: scrollOffset,
                behavior: 'smooth'
              });
            }
          }
        };
        // First scroll after expansion renders, second after animation settles
        setTimeout(scrollToCategory, 500);
        setTimeout(scrollToCategory, 900);
      }
    };
    // Check on mount (for cross-page navigation)
    handleMaterialsNav();
    // Listen for same-page navigation
    window.addEventListener('materialsNav', handleMaterialsNav);
    const handleCollapse = () => {
      setExpandedCategory(null);
    };
    const handleReset = () => {
      setExpandedCategory(null);
      setActiveTab('materials');
    };
    window.addEventListener('materialsCollapse', handleCollapse);
    window.addEventListener('materialsReset', handleReset);
    return () => {
      window.removeEventListener('materialsNav', handleMaterialsNav);
      window.removeEventListener('materialsCollapse', handleCollapse);
      window.removeEventListener('materialsReset', handleReset);
    };
  }, []);
  const handlePrev = (categoryName: string) => {
    const category = categories.find(cat => cat.name === categoryName);
    if (!category) return;
    setCarouselIndex(prev => prev === 0 ? category.products.length - 1 : prev - 1);
  };
  const handleNext = (categoryName: string) => {
    const category = categories.find(cat => cat.name === categoryName);
    if (!category) return;
    setCarouselIndex(prev => prev === category.products.length - 1 ? 0 : prev + 1);
  };
  const setDotIndex = (index: number) => {
    setCarouselIndex(index);
  };
  return <>
      <section className="relative py-24 bg-[#fdfbf7] dark:bg-[#242322] transition-colors duration-300 overflow-hidden border-t border-transparent dark:border-[#404040]" id="materials" data-id="element-907">
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-[#e8e0d2]/20 dark:from-[#3a3633]/20 to-transparent pointer-events-none" data-id="element-908" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-909">
          {/* Header */}
          <div className="mb-20" data-id="element-910">
            <motion.div initial={{
            width: 0,
            opacity: 0
          }} whileInView={{
            width: 64,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            width: {
              duration: 1.2,
              ease: [0.22, 1, 0.36, 1],
              delay: 1
            },
            opacity: {
              duration: 0.4,
              delay: 1
            }
          }} className="h-1 bg-brand-orange rounded-full mb-8" data-id="element-911" />

            <div className="flex flex-col md:flex-row md:items-baseline gap-4 md:gap-8 mb-6" data-id="element-912">
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 20,
              delay: 0.1
            }} className="font-heading text-5xl md:text-6xl lg:text-7xl font-extrabold text-brand-charcoal dark:text-white tracking-tight leading-[1.1]" data-id="element-913">
                Materials <span className="text-brand-orange" data-id="element-914">Hub</span>
              </motion.h2>

              <motion.span initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.15
            }} className="inline-block text-xs font-bold tracking-[0.2em] uppercase self-center text-[#1DB954]" data-id="element-915">
                — Explore Our Catalog
              </motion.span>
            </div>

            <motion.p initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20,
            delay: 0.25
          }} className="text-brand-charcoal dark:text-white text-lg font-medium font-sub leading-relaxed max-w-lg tracking-wide" data-id="element-916">
              Browse our catalog, estimate your needs, or find the right
              material for your next project.
            </motion.p>
          </div>

          {/* Tab Content */}
          <div className="min-h-[400px]" data-id="element-917">
            <AnimatePresence mode="wait" data-id="element-918">
              {/* MATERIALS TAB */}
              {activeTab === 'materials' && <motion.div key="materials" initial={{
              opacity: 0,
              y: 20,
              scale: 0.98
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: -20,
              scale: 0.98
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 25
            }} className="space-y-0 divide-y divide-stone-200 dark:divide-[#4a4a4a]" data-id="element-919">
                  {categories.map(category => {
                const isExpanded = expandedCategory === category.name;
                if (isExpanded) {
                  // Single-product carousel UI replacing the product grid
                  const product = category.products[carouselIndex] || null;
                  return <div key={category.name} id={`cat-${category.name}`} className="overflow-hidden scroll-mt-[135px]" data-id="element-920">
                          <button onClick={() => {
                      if (isExpanded) {
                        setExpandedCategory(null);
                        setCategoryOverview(null);
                      } else {
                        setExpandedCategory(category.name);
                        setCategoryOverview(category.name);
                        setCarouselIndex(0);
                      }
                    }} className="w-full flex items-center justify-between py-3 sm:py-4 text-left focus:outline-none group" data-id="element-921">
                            <div data-id="element-922">
                              <h3 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight transition-colors duration-200 ${isExpanded ? 'text-brand-orange' : 'text-brand-charcoal dark:text-white/80 group-hover:text-brand-orange'}`} data-id="element-923">
                                {category.name}
                              </h3>
                              <span className="text-[11px] font-normal text-stone-400 dark:text-white/40 tracking-wide" data-id="element-924">
                                {category.name === 'Soil, Mulch & Org.' ? '15' : '12'}{' '}
                                yards max load
                              </span>
                            </div>
                            <ChevronDown className={`w-7 h-7 shrink-0 ml-4 transition-transform duration-300 ${isExpanded ? 'rotate-180 text-brand-orange' : 'text-brand-charcoal/30 dark:text-white/30 group-hover:text-brand-orange'}`} data-id="element-925" />
                          </button>

                          <AnimatePresence mode="wait" data-id="element-926">
                            {isExpanded && product && <motion.div initial={{
                        opacity: 0,
                        height: 0
                      }} animate={{
                        opacity: 1,
                        height: 'auto'
                      }} exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.3,
                        ease: 'easeInOut'
                      }} data-id="element-927">
                                <div className="pb-10" data-id="element-928">
                                  {/* Category Overview - Large Stacked Image Cards */}
                                  {categoryOverview === category.name ? <motion.div key="category-overview" initial={{
                            opacity: 0,
                            y: 10
                          }} animate={{
                            opacity: 1,
                            y: 0
                          }} exit={{
                            opacity: 0,
                            y: -10
                          }} transition={{
                            duration: 0.25
                          }} className="space-y-1.5" data-id="element-929">
                                      {category.products.map((p, idx) => <motion.button key={p.slug} initial={{
                              opacity: 0,
                              y: 20
                            }} animate={{
                              opacity: 1,
                              y: 0
                            }} transition={{
                              duration: 0.3,
                              delay: idx * 0.06
                            }} onClick={() => {
                              setCategoryOverview(null);
                              setCarouselIndex(idx);
                              setCategoryGridView(null);
                              setTimeout(() => {
                                const catEl = document.getElementById(`cat-${category.name}`);
                                if (catEl) {
                                  const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                                  const top = catEl.getBoundingClientRect().top + window.scrollY - navHeight - 20;
                                  window.scrollTo({
                                    top,
                                    behavior: 'smooth'
                                  });
                                }
                              }, 50);
                            }} className="w-full relative overflow-hidden rounded-xl group/overview block text-left" style={{
                              height: '200px'
                            }} data-id="element-930">
                                          <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover/overview:scale-105 transition-transform duration-500" data-id="element-931" />
                                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-932" />
                                          {TOP_SELLER_SLUGS.has(p.slug) && <div className="absolute top-3 left-3 z-10" data-id="element-933">
                                              <span className="bg-brand-orange text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 shadow-md animate-shimmer block" data-id="element-934">
                                                Top Seller
                                              </span>
                                            </div>}
                                          <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6" data-id="element-935">
                                            <h4 className="font-heading text-2xl sm:text-3xl font-bold text-white leading-tight mb-1" data-id="element-936">
                                              {p.name}
                                            </h4>
                                            <p className="text-white/90 text-sm sm:text-base font-medium" data-id="element-937">
                                              ${p.pricePerYard} / yard
                                            </p>
                                          </div>
                                        </motion.button>)}
                                    </motion.div> : <>
                                      {/* Category Items Carousel */}
                                      <div className="mb-6 flex items-center gap-2" data-id="element-938">
                                        <div className="relative flex-1 min-w-0" data-id="element-939">
                                          <div className="overflow-x-auto scrollbar-hide -mx-1 px-1" data-id="element-940">
                                            <div className="flex items-center gap-1 whitespace-nowrap pb-1" style={{
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none'
                                  }} data-id="element-941">
                                              {category.products.map((p, idx) => <button key={p.name} onClick={e => {
                                      setCategoryGridView(null);
                                      setCarouselIndex(idx);
                                      setAddingToCart(null);
                                      // Auto-scroll to reveal next tab if this one is near the edge
                                      const btn = e.currentTarget;
                                      const scrollContainer = btn.parentElement?.parentElement;
                                      if (scrollContainer) {
                                        const nextBtn = btn.nextElementSibling as HTMLElement | null;
                                        if (nextBtn) {
                                          nextBtn.scrollIntoView({
                                            behavior: 'smooth',
                                            block: 'nearest',
                                            inline: 'nearest'
                                          });
                                        } else {
                                          btn.scrollIntoView({
                                            behavior: 'smooth',
                                            block: 'nearest',
                                            inline: 'nearest'
                                          });
                                        }
                                      }
                                    }} className={`relative shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${categoryGridView === category.name ? 'text-stone-600 dark:text-stone-200 hover:text-white' : idx === carouselIndex ? 'text-brand-orange bg-brand-orange/10' : 'text-stone-600 dark:text-white/80 hover:text-white hover:bg-white/5'}`} data-id="element-942">
                                                    {p.name}
                                                    {idx === carouselIndex && categoryGridView !== category.name && <motion.div layoutId="activeProduct" className="absolute bottom-0 left-2 right-2 h-[2px] bg-brand-orange rounded-full" transition={{
                                        type: 'spring',
                                        stiffness: 400,
                                        damping: 25
                                      }} data-id="element-943" />}
                                                  </button>)}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="shrink-0 border-l border-stone-300 dark:border-white/10 pl-3 ml-1" data-id="element-944">
                                          <button onClick={() => setCategoryOverview(category.name)} className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all bg-stone-200 dark:bg-white/10 text-stone-800 dark:text-stone-300 hover:bg-brand-orange/15 hover:text-brand-orange" data-id="element-945">
                                            ← Back
                                          </button>
                                        </div>
                                      </div>

                                      {/* Category Grid View */}
                                      {categoryGridView === category.name ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 pb-4" data-id="element-946">
                                          {category.products.map(p => <button key={p.slug} onClick={() => {
                                setCategoryGridView(null);
                                setCarouselIndex(category.products.findIndex(pr => pr.slug === p.slug));
                                setTimeout(() => {
                                  const catEl = document.getElementById(`cat-${category.name}`);
                                  if (catEl) {
                                    const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                                    const top = catEl.getBoundingClientRect().top + window.scrollY - navHeight - 20;
                                    window.scrollTo({
                                      top,
                                      behavior: 'smooth'
                                    });
                                  }
                                }, 50);
                              }} className="bg-stone-200 dark:bg-[#282828] rounded-xl overflow-hidden border border-stone-400 dark:border-[#383838] hover:border-brand-orange/50 transition-all group/card text-left flex flex-col" data-id="element-947">
                                              <div className="aspect-[4/3] overflow-hidden relative" data-id="element-948">
                                                <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500" data-id="element-949" />
                                                <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-xs font-bold" data-id="element-950">
                                                  ${p.pricePerYard}/yd
                                                </div>
                                              </div>
                                              <div className="p-4" data-id="element-951">
                                                <h4 className="font-bold text-brand-charcoal dark:text-white text-sm" data-id="element-952">
                                                  {p.name}
                                                </h4>
                                                <p className="text-xs text-stone-600 dark:text-stone-200 mt-1 line-clamp-2" data-id="element-953">
                                                  {p.description}
                                                </p>
                                              </div>
                                            </button>)}
                                        </div> : <div className="bg-[#faf8f6] dark:bg-[#353433] md:rounded-2xl md:border border-stone-200 dark:border-[#424242] overflow-hidden flex flex-col md:flex-row shadow-2xl md:shadow-[0_-8px_15px_-8px_rgba(255,255,255,0.08)] -mx-4 sm:mx-0 relative touch-pan-y" onTouchStart={e => {
                              const touch = e.touches[0];
                              (e.currentTarget as any)._swipeStartX = touch.clientX;
                              (e.currentTarget as any)._swipeStartY = touch.clientY;
                            }} onTouchEnd={e => {
                              const startX = (e.currentTarget as any)._swipeStartX;
                              const startY = (e.currentTarget as any)._swipeStartY;
                              if (startX == null || startY == null) return;
                              const touch = e.changedTouches[0];
                              const deltaX = touch.clientX - startX;
                              const deltaY = touch.clientY - startY;
                              // Only trigger if horizontal swipe is dominant and significant
                              if (Math.abs(deltaX) > 100 && Math.abs(deltaX) > Math.abs(deltaY) * 2) {
                                if (deltaX < 0) {
                                  handleNext(category.name);
                                } else {
                                  handlePrev(category.name);
                                }
                                // Scroll the active product tab into view
                                setTimeout(() => {
                                  const catEl = document.getElementById(`cat-${category.name}`);
                                  if (catEl) {
                                    const tabContainer = catEl.querySelector('.overflow-x-auto');
                                    if (tabContainer) {
                                      const activeBtn = tabContainer.querySelector('.text-brand-orange') as HTMLElement;
                                      if (activeBtn) {
                                        activeBtn.scrollIntoView({
                                          behavior: 'smooth',
                                          block: 'nearest',
                                          inline: 'nearest'
                                        });
                                      }
                                    }
                                  }
                                }, 50);
                              }
                              ;
                              (e.currentTarget as any)._swipeStartX = null;
                              (e.currentTarget as any)._swipeStartY = null;
                            }} data-id="element-954">
                                          {/* Image — 2:3 ratio on mobile */}
                                          <div className="w-full md:w-1/2 aspect-[3/4] md:aspect-auto md:h-auto md:min-h-[400px] relative" data-id="element-955">
                                            <ProductImageCarousel images={product.images || [product.image]} alt={product.name} data-id="element-956" />
                                            {TOP_SELLER_SLUGS.has(product.slug) && <div className="absolute top-3 left-3 z-20" data-id="element-957">
                                                <span className="bg-brand-orange text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 shadow-md animate-shimmer block" data-id="element-958">
                                                  Top Seller
                                                </span>
                                              </div>}
                                            {/* Gradient fade for mobile with overlaid product name */}
                                            <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#faf8f6] dark:from-[#353433] via-[#faf8f6]/60 dark:via-[#353433]/60 to-transparent pointer-events-none md:hidden" data-id="element-959" />
                                            <div className="absolute bottom-0 left-0 right-0 p-6 z-10 md:hidden" data-id="element-960">
                                              <span className="text-xs font-bold text-brand-muted uppercase tracking-wider mb-1 block drop-shadow-md" data-id="element-961">
                                                {category.name}
                                              </span>
                                              <h4 className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white drop-shadow-md" data-id="element-962">
                                                {product.name}
                                              </h4>
                                            </div>
                                          </div>

                                          {/* Details */}
                                          <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-10 flex flex-col relative z-10 -mt-6 md:mt-0" data-id="element-963">
                                            <span className="hidden md:block text-xs font-bold text-brand-muted uppercase tracking-wider mb-2" data-id="element-964">
                                              {category.name}
                                            </span>
                                            <h4 className="hidden md:block font-heading text-3xl md:text-4xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-965">
                                              {product.name}
                                            </h4>
                                            <p className="text-brand-charcoal/80 dark:text-white/90 text-base leading-relaxed mb-4" data-id="element-966">
                                              {product.description}
                                            </p>

                                            {/* Best For Tags */}
                                            {product.bestFor && product.bestFor.length > 0 && <div className="flex flex-wrap gap-1.5 mb-4" data-id="element-967">
                                                  {product.bestFor.map((tag, idx) => <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full bg-brand-lightgray dark:bg-[#2a2a2a] text-xs font-medium text-brand-charcoal dark:text-stone-300 border border-stone-200 dark:border-[#444444]" data-id="element-968">
                                                        {tag}
                                                      </span>)}
                                                </div>}

                                            {/* Pro Tip */}
                                            {product.proTip && <div className="bg-brand-orange/10 border border-brand-orange/20 p-3 rounded-lg mb-4 flex items-start gap-2" data-id="element-969">
                                                <Info className="w-3.5 h-3.5 text-brand-orange mt-0.5 shrink-0" data-id="element-970" />
                                                <p className="text-xs text-brand-charcoal/80 dark:text-white/80 leading-relaxed" data-id="element-971">
                                                  <span className="font-bold" data-id="element-972">
                                                    Pro Tip:
                                                  </span>{' '}
                                                  {product.proTip}
                                                </p>
                                              </div>}

                                            {/* View Full Details Link */}
                                            <Link to={`/shop/${product.slug}`} className="inline-flex items-center text-sm font-medium text-brand-orange hover:text-brand-orange/80 transition-colors mb-6 group" data-id="element-973">
                                              View Full Specs & Details
                                              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" data-id="element-974" />
                                            </Link>

                                            {category.name !== 'Specialty Rock' && product.pricePerYard > 0 ? <>
                                                <div className="mb-6 pb-6 border-b border-stone-200/50 dark:border-[#4a4a4a]/50" data-id="element-975">
                                                  {(() => {
                                      const isBarkMulch = isLightMaterial(product.name);
                                      const qtyNum = selectedQuantity;
                                      const fullLoadYards = isBarkMulch ? 15 : 12;
                                      const minChargeYards = 5;
                                      const smallLoadThreshold = isBarkMulch ? 12 : 10;
                                      // Helper: calculate total for a single-load quantity
                                      const calcLoadTotal = (yards: number) => {
                                        const billableYards = Math.max(yards, minChargeYards);
                                        const surcharge = billableYards < smallLoadThreshold ? 1 + (smallLoadThreshold - billableYards) * 0.1 : 1;
                                        const price = Math.ceil(product.pricePerYard * surcharge);
                                        return billableYards * price;
                                      };
                                      // Helper: get effective per-yard price for display
                                      const calcEffectivePrice = (yards: number) => {
                                        const billableYards = Math.max(yards, minChargeYards);
                                        const surcharge = billableYards < smallLoadThreshold ? 1 + (smallLoadThreshold - billableYards) * 0.1 : 1;
                                        return Math.ceil(product.pricePerYard * surcharge);
                                      };
                                      const isBeyondFullLoad = qtyNum > fullLoadYards;
                                      const isBelowMinCharge = qtyNum < minChargeYards;
                                      const displayYards = isBeyondFullLoad ? fullLoadYards : qtyNum;
                                      const effectivePrice = calcEffectivePrice(displayYards);
                                      const isSmallLoad = !isBeyondFullLoad && qtyNum < smallLoadThreshold;
                                      // Tiered bulk discount: light materials 5% at 15yds, others 5% at 24yds; 10% at 4 full loads
                                      const bulkDiscountPct = qtyNum >= fullLoadYards * 4 ? 0.1 : qtyNum >= (isBarkMulch ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
                                      const hasBulkDiscount = bulkDiscountPct > 0;
                                      // Build delivery loads
                                      const deliveryLoads: {
                                        yards: number;
                                        total: number;
                                      }[] = [];
                                      if (isBeyondFullLoad) {
                                        let remaining = qtyNum;
                                        while (remaining > 0) {
                                          const loadYards = Math.min(remaining, fullLoadYards);
                                          deliveryLoads.push({
                                            yards: loadYards,
                                            total: calcLoadTotal(loadYards)
                                          });
                                          remaining -= loadYards;
                                        }
                                      }
                                      let total = isBeyondFullLoad ? deliveryLoads.reduce((sum, l) => sum + l.total, 0) : calcLoadTotal(qtyNum);
                                      // Apply tiered bulk discount
                                      const preDiscountTotal = total;
                                      if (hasBulkDiscount) {
                                        total = Math.round(total * (1 - bulkDiscountPct));
                                        deliveryLoads.forEach((l, i) => {
                                          deliveryLoads[i] = {
                                            ...l,
                                            total: Math.round(l.total * (1 - bulkDiscountPct))
                                          };
                                        });
                                      }
                                      const extraYards = isBeyondFullLoad ? qtyNum - fullLoadYards : 0;
                                      const extraBillable = Math.max(extraYards, minChargeYards);
                                      const extraEffectivePrice = isBeyondFullLoad ? calcEffectivePrice(extraYards) : 0;
                                      const hasMessages = hasBulkDiscount || isBelowMinCharge || isSmallLoad || isBeyondFullLoad;
                                      return <div className="bg-stone-100/70 dark:bg-black/20 backdrop-blur-md rounded-xl p-4 sm:p-5 border border-stone-200 dark:border-white/5 shadow-sm" data-id="element-976">
                                                        <div className="flex items-center justify-between gap-3" data-id="element-977">
                                                          <div className="flex items-baseline gap-1" data-id="element-978">
                                                            <span className="font-heading text-3xl sm:text-4xl font-bold text-brand-charcoal dark:text-white" data-id="element-979">
                                                              $
                                                              {hasBulkDiscount ? Math.ceil(effectivePrice * (1 - bulkDiscountPct)) : effectivePrice}
                                                            </span>
                                                            <span className="text-brand-charcoal/60 dark:text-white/60 text-sm font-medium" data-id="element-980">
                                                              /yd
                                                            </span>
                                                          </div>

                                                          <div className="flex items-baseline gap-1.5 text-right" data-id="element-981">
                                                            <span className="text-brand-charcoal/80 dark:text-white/80 text-sm font-medium" data-id="element-982">
                                                              {qtyNum} yds =
                                                            </span>
                                                            <span className="font-heading font-bold text-brand-charcoal dark:text-white text-xl" data-id="element-983">
                                                              ${total}
                                                            </span>
                                                            <span className="text-brand-charcoal/70 dark:text-white/70 text-sm" data-id="element-984">
                                                              +del
                                                            </span>
                                                          </div>
                                                        </div>

                                                        {hasMessages && <div className="mt-4 pt-3 border-t border-brand-charcoal/5 dark:border-white/5 space-y-1.5" data-id="element-985">
                                                            {hasBulkDiscount && <div className="flex items-center justify-between" data-id="element-986">
                                                                <p className="text-xs text-green-600 dark:text-green-400 font-semibold" data-id="element-987">
                                                                  {Math.round(bulkDiscountPct * 100)}
                                                                  % bulk
                                                                  discount
                                                                  applied (
                                                                  {qtyNum >= 48 ? '48' : '24'}
                                                                  + yards)
                                                                </p>
                                                                <p className="text-xs text-brand-charcoal/40 dark:text-white/40 line-through" data-id="element-988">
                                                                  Was $
                                                                  {preDiscountTotal}
                                                                </p>
                                                              </div>}
                                                            {!isBeyondFullLoad && isBelowMinCharge && <p className="text-xs text-brand-orange" data-id="element-989">
                                                                  {minChargeYards}{' '}
                                                                  yard minimum
                                                                  charge applies
                                                                </p>}
                                                            {isSmallLoad && !isBelowMinCharge && <p className="text-xs text-brand-orange" data-id="element-990">
                                                                  Small load
                                                                  upcharge · $
                                                                  {product.pricePerYard}
                                                                  /yd at{' '}
                                                                  {smallLoadThreshold}
                                                                  + yards
                                                                </p>}
                                                            {isBeyondFullLoad && deliveryLoads.length > 0 && <div className="space-y-1" data-id="element-991">
                                                                  {deliveryLoads.map((load, idx) => {
                                              const loadBillable = Math.max(load.yards, minChargeYards);
                                              const showCharged = load.yards < loadBillable;
                                              return <div key={idx} className="flex justify-between text-xs text-brand-charcoal/70 dark:text-white/70" data-id="element-992">
                                                                          <span data-id="element-993">
                                                                            Delivery{' '}
                                                                            {idx + 1}
                                                                            :{' '}
                                                                            {load.yards}{' '}
                                                                            yds
                                                                            {showCharged && ` (charged as ${loadBillable})`}
                                                                          </span>
                                                                          <span className="font-bold text-brand-charcoal dark:text-white" data-id="element-994">
                                                                            $
                                                                            {load.total}
                                                                          </span>
                                                                        </div>;
                                            })}
                                                                  {deliveryLoads.length > 1 && <p className="text-xs text-brand-orange mt-1" data-id="element-995">
                                                                      +
                                                                      additional
                                                                      delivery
                                                                      fee for{' '}
                                                                      {deliveryLoads.length - 1}{' '}
                                                                      extra{' '}
                                                                      {deliveryLoads.length > 2 ? 'loads' : 'load'}
                                                                    </p>}
                                                                </div>}
                                                          </div>}
                                                      </div>;
                                    })()}
                                                </div>

                                                <div className="space-y-4 mt-auto" data-id="element-996">
                                                  {addedFlash === product.name ? <motion.div initial={{
                                      opacity: 0,
                                      y: 10
                                    }} animate={{
                                      opacity: 1,
                                      y: 0
                                    }} className="space-y-3" data-id="element-997">
                                                      <div className="flex items-center justify-center w-full py-3 bg-green-500/10 dark:bg-green-500/20 text-green-600 dark:text-green-400 font-bold text-sm border border-green-500/30 rounded-xl" data-id="element-998">
                                                        <Check className="w-4 h-4 mr-2" data-id="element-999" />
                                                        Added to Cart!
                                                      </div>
                                                      <div className="flex gap-2" data-id="element-1000">
                                                        <button onClick={() => {
                                          setAddedFlash(null);
                                          setExpandedCategory(null);
                                          setCategoryOverview(null);
                                          setTimeout(() => {
                                            const el = document.getElementById('materials');
                                            if (el) {
                                              const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                                              const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
                                              window.scrollTo({
                                                top,
                                                behavior: 'smooth'
                                              });
                                            }
                                          }, 100);
                                        }} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-brand-charcoal dark:text-white border border-stone-200 dark:border-white/10 bg-white/50 dark:bg-white/5 hover:bg-white/80 dark:hover:bg-white/10 transition-colors" data-id="element-1001">
                                                          Continue Shopping
                                                        </button>
                                                        <button onClick={() => {
                                          setAddedFlash(null);
                                          openCart();
                                        }} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-brand-orange hover:bg-brand-orangeHover transition-colors flex items-center justify-center gap-1.5" data-id="element-1002">
                                                          <ShoppingCart className="w-4 h-4" data-id="element-1003" />
                                                          View Cart
                                                        </button>
                                                      </div>
                                                    </motion.div> : <>
                                                      <div data-id="element-1004">
                                                        <span className="block text-sm font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-1005">
                                                          Select Quantity
                                                        </span>
                                                        <div className="flex gap-3" data-id="element-1006">
                                                          {/* Quantity Stepper */}
                                                          <div className="relative w-1/2" data-id="element-1007">
                                                            <div className="flex items-center bg-stone-100 dark:bg-[#303030] border border-stone-300 dark:border-[#4b4b4b] rounded-none" data-id="element-1008">
                                                              <button type="button" onClick={() => {
                                                if (selectedQuantity > 5) {
                                                  setSelectedQuantity(selectedQuantity - 1);
                                                }
                                              }} disabled={selectedQuantity <= 5} className="px-3 py-3 text-stone-800 dark:text-white hover:bg-stone-200 dark:hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1009">
                                                                <Minus className="w-4 h-4" data-id="element-1010" />
                                                              </button>

                                                              <button type="button" onClick={() => setShowQuantityDropdown(!showQuantityDropdown)} className="flex-1 px-2 py-3 text-stone-800 dark:text-white font-medium text-center hover:bg-stone-200/60 dark:hover:bg-white/5 transition-colors" data-id="element-1011">
                                                                {selectedQuantity}{' '}
                                                                Yards
                                                              </button>

                                                              <button type="button" onClick={() => {
                                                const isLight = isLightMaterial(product.name);
                                                const maxQuantity = isLight ? 60 : 48;
                                                if (selectedQuantity >= maxQuantity) {
                                                  setIsQuoteModalOpen(true);
                                                } else {
                                                  setSelectedQuantity(selectedQuantity + 1);
                                                }
                                              }} className="px-3 py-3 text-stone-800 dark:text-white hover:bg-stone-200 dark:hover:bg-white/10 transition-colors" data-id="element-1012">
                                                                <Plus className="w-4 h-4" data-id="element-1013" />
                                                              </button>
                                                            </div>

                                                            {/* Quantity Dropdown Popup */}
                                                            <AnimatePresence data-id="element-1014">
                                                              {showQuantityDropdown && <motion.div initial={{
                                                opacity: 0,
                                                y: -10
                                              }} animate={{
                                                opacity: 1,
                                                y: 0
                                              }} exit={{
                                                opacity: 0,
                                                y: -10
                                              }} transition={{
                                                duration: 0.15
                                              }} className="absolute z-50 w-full bottom-full mb-2 bg-white dark:bg-[#383838] border border-stone-200 dark:border-[#4b4b4b] rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-id="element-1015">
                                                                  {(() => {
                                                  const isLight = isLightMaterial(product.name);
                                                  const fullLoad = isLight ? 15 : 12;
                                                  const maxQty = isLight ? 60 : 48;
                                                  const options = [];
                                                  for (let i = 5; i <= maxQty; i++) {
                                                    let label = `${i} Yards`;
                                                    if (i === fullLoad) label += ' (Full Load)';
                                                    options.push({
                                                      value: i,
                                                      label
                                                    });
                                                  }
                                                  return options.map(opt => <button key={opt.value} type="button" onClick={() => {
                                                    setSelectedQuantity(opt.value);
                                                    setShowQuantityDropdown(false);
                                                  }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${selectedQuantity === opt.value ? 'bg-brand-orange/20 text-brand-orange' : 'text-brand-charcoal dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-1016">
                                                                          {opt.label}
                                                                        </button>);
                                                })()}
                                                                </motion.div>}
                                                            </AnimatePresence>
                                                          </div>

                                                          <button onClick={() => handleConfirmAdd(category.name, product.name, product.pricePerYard)} className="flex-1 py-3 rounded-none font-bold transition-all duration-300 flex items-center justify-center bg-brand-button hover:bg-brand-buttonHover text-white shadow-[0_0_12px_rgba(5,137,77,0.4)]" data-id="element-1017">
                                                            <ShoppingCart className="w-5 h-5 mr-2" data-id="element-1018" />
                                                            Add to Cart
                                                          </button>
                                                        </div>
                                                      </div>

                                                      {/* Inline Calculator */}
                                                      <div className="mt-4" data-id="element-1019">
                                                        <button onClick={() => setShowInlineCalc(!showInlineCalc)} className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-brand-orange hover:text-brand-orangeHover transition-colors" data-id="element-1020">
                                                          <Calculator className="w-4 h-4" data-id="element-1021" />
                                                          {showInlineCalc ? 'Hide Calculator' : 'Calculate how much I need'}
                                                        </button>

                                                        <AnimatePresence data-id="element-1022">
                                                          {showInlineCalc && <motion.div initial={{
                                            opacity: 0,
                                            height: 0
                                          }} animate={{
                                            opacity: 1,
                                            height: 'auto'
                                          }} exit={{
                                            opacity: 0,
                                            height: 0
                                          }} className="overflow-hidden" data-id="element-1023">
                                                              <div className="p-4 mt-2 bg-stone-50 dark:bg-[#303030] border border-stone-200 dark:border-[#4b4b4b] rounded-xl space-y-4" data-id="element-1024">
                                                                <div className="grid grid-cols-3 gap-3" data-id="element-1025">
                                                                  <div data-id="element-1026">
                                                                    <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1027">
                                                                      Length
                                                                      (ft)
                                                                    </label>
                                                                    <input type="number" value={inlineCalcLength} onChange={e => setInlineCalcLength(e.target.value)} placeholder="0" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1028" />
                                                                  </div>
                                                                  <div data-id="element-1029">
                                                                    <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1030">
                                                                      Width (ft)
                                                                    </label>
                                                                    <input type="number" value={inlineCalcWidth} onChange={e => setInlineCalcWidth(e.target.value)} placeholder="0" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1031" />
                                                                  </div>
                                                                  <div data-id="element-1032">
                                                                    <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1033">
                                                                      Depth (in)
                                                                    </label>
                                                                    <input type="number" value={inlineCalcDepth} onChange={e => setInlineCalcDepth(e.target.value)} placeholder="3" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1034" />
                                                                  </div>
                                                                </div>

                                                                {(() => {
                                                const l = parseFloat(inlineCalcLength) || 0;
                                                const w = parseFloat(inlineCalcWidth) || 0;
                                                const d = parseFloat(inlineCalcDepth) || 0;
                                                const cubicYards = l * w * (d / 12) / 27;
                                                const roundedYards = Math.max(1, Math.ceil(cubicYards));
                                                if (l > 0 && w > 0 && d > 0) {
                                                  return <div className="flex items-center justify-between pt-3 border-t border-stone-200 dark:border-[#404040]" data-id="element-1035">
                                                                        <div data-id="element-1036">
                                                                          <span className="text-xs text-stone-500 dark:text-stone-400" data-id="element-1037">
                                                                            Estimated
                                                                            need:
                                                                          </span>
                                                                          <div className="font-bold text-stone-900 dark:text-white" data-id="element-1038">
                                                                            {cubicYards.toFixed(1)}{' '}
                                                                            yards
                                                                          </div>
                                                                        </div>
                                                                        <button onClick={() => {
                                                      setSelectedQuantity(roundedYards);
                                                      setShowInlineCalc(false);
                                                    }} className="px-4 py-2 bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal text-sm font-bold rounded-lg hover:bg-stone-800 dark:hover:bg-stone-200 transition-colors" data-id="element-1039">
                                                                          Use{' '}
                                                                          {roundedYards}{' '}
                                                                          Yards
                                                                        </button>
                                                                      </div>;
                                                }
                                                return null;
                                              })()}
                                                              </div>
                                                            </motion.div>}
                                                        </AnimatePresence>
                                                      </div>

                                                      <p className="text-xs text-brand-charcoal/70 dark:text-white/70 text-center mt-4" data-id="element-1040">
                                                        Need a custom amount?{' '}
                                                        <a href="tel:206-619-3742" className="text-brand-orange hover:underline" data-id="element-1041">
                                                          Call us
                                                        </a>{' '}
                                                        or{' '}
                                                        <Link to="/contact" className="text-brand-orange hover:underline" data-id="element-1042">
                                                          contact us
                                                        </Link>
                                                        .
                                                      </p>

                                                      <div className="flex items-center justify-center gap-3 mt-4 pt-4 border-t border-stone-200 dark:border-[#3a3a3a]" data-id="element-1043">
                                                        <button onClick={() => {
                                          setCalcResults(null);
                                          setShowCalcModal(true);
                                        }} className="flex items-center gap-1.5 text-sm font-semibold text-stone-800 dark:text-white/80 hover:text-brand-orange dark:hover:text-brand-orange transition-colors px-3 py-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-white/5" data-id="element-1044">
                                                          <Calculator className="w-4 h-4 text-[#d4a017]" data-id="element-1045" />
                                                          Calculator
                                                        </button>
                                                        <span className="text-stone-300 dark:text-stone-600" data-id="element-1046">
                                                          |
                                                        </span>
                                                        <button onClick={() => {
                                          resetQuiz();
                                          setShowQuizModal(true);
                                        }} className="flex items-center gap-1.5 text-sm font-semibold text-stone-800 dark:text-white/80 hover:text-brand-orange dark:hover:text-brand-orange transition-colors px-3 py-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-white/5" data-id="element-1047">
                                                          <ArrowRight className="w-4 h-4 text-[#d4a017]" data-id="element-1048" />
                                                          Help Me Choose
                                                        </button>
                                                      </div>
                                                    </>}
                                                </div>
                                              </> : <div className="mt-auto space-y-4" data-id="element-1049">
                                                <a href="tel:206-619-3742" className="w-full bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal hover:bg-brand-charcoal/90 dark:hover:bg-stone-200 py-3.5 rounded-xl font-bold transition-colors flex items-center justify-center" data-id="element-1050">
                                                  <Phone className="w-5 h-5 mr-2" data-id="element-1051" />
                                                  Call for Pricing
                                                </a>
                                                <button onClick={() => setIsQuoteModalOpen(true)} className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-3.5 rounded-xl font-bold transition-colors" data-id="element-1052">
                                                  Get a Custom Quote
                                                </button>
                                              </div>}
                                          </div>
                                        </div>}
                                    </>}
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </div>;
                }
                return <div key={category.name} id={`cat-${category.name}`} className="overflow-hidden scroll-mt-[135px]" data-id="element-1053">
                        <button onClick={() => {
                    setExpandedCategory(category.name);
                    setCategoryOverview(category.name);
                    setCarouselIndex(0);
                    setTimeout(() => {
                      document.getElementById(`cat-${category.name}`)?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }, 100);
                  }} className="w-full flex items-center justify-between py-3 sm:py-4 text-left focus:outline-none group" data-id="element-1054">
                          <div data-id="element-1055">
                            <h3 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight transition-colors duration-200 text-brand-charcoal dark:text-white/80 group-hover:text-brand-orange`} data-id="element-1056">
                              {category.name}
                            </h3>
                            <span className="text-[11px] font-normal text-stone-400 dark:text-white/40 tracking-wide" data-id="element-1057">
                              {category.name === 'Soil, Mulch & Org.' ? '15' : '12'}{' '}
                              yards max load
                            </span>
                          </div>
                          <ChevronDown className="w-7 h-7 shrink-0 ml-4 transition-transform duration-300 text-brand-charcoal/30 dark:text-white/30 group-hover:text-brand-orange" data-id="element-1058" />
                        </button>
                      </div>;
              })}

                  {/* View Full Catalog Button & Expanded Grid */}
                  <div className="pt-6" data-id="element-1059">
                    <button onClick={() => {
                  const wasOpen = showAllCatalog;
                  setShowAllCatalog(!showAllCatalog);
                  if (wasOpen) {
                    setTimeout(() => {
                      document.getElementById('materials')?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }, 450);
                  }
                }} className="mx-auto flex items-center gap-2 py-2 px-4 text-stone-800 dark:text-stone-300 hover:text-brand-orange transition-colors text-xs uppercase tracking-wider" data-id="element-1060">
                      <motion.span animate={{
                    rotate: showAllCatalog ? 45 : 0
                  }} transition={{
                    duration: 0.2
                  }} className="w-5 h-5 flex items-center justify-center" data-id="element-1061">
                        <span className="text-sm font-bold leading-none" data-id="element-1062">
                          +
                        </span>
                      </motion.span>
                      <span className="font-bold text-sm uppercase tracking-wider" data-id="element-1063">
                        {showAllCatalog ? 'Collapse Catalog' : 'View Full Catalog'}
                      </span>
                    </button>

                    <AnimatePresence data-id="element-1064">
                      {showAllCatalog && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.4,
                    ease: 'easeInOut'
                  }} className="overflow-hidden" data-id="element-1065">
                          <div className="pt-8 space-y-12" data-id="element-1066">
                            {categories.map(category => <div key={`catalog-${category.name}`} data-id="element-1067">
                                <h3 className="font-heading text-2xl font-bold text-brand-charcoal dark:text-white mb-5 flex items-center" data-id="element-1068">
                                  <span className="w-2 h-7 bg-brand-orange mr-3 rounded-sm" data-id="element-1069" />
                                  {category.name}
                                </h3>
                                <div className="space-y-1.5" data-id="element-1070">
                                  {category.products.map(product => <button key={product.slug} onClick={() => {
                            setShowAllCatalog(false);
                            setExpandedCategory(category.name);
                            setCategoryOverview(null);
                            setCarouselIndex(category.products.findIndex(p => p.slug === product.slug));
                            setTimeout(() => {
                              document.getElementById(`cat-${category.name}`)?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start'
                              });
                            }, 100);
                          }} className="w-full relative overflow-hidden rounded-xl group/card text-left block" style={{
                            height: '200px'
                          }} data-id="element-1071">
                                      <img src={product.images?.[0] || product.image} alt={product.name} className="absolute inset-0 w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500" data-id="element-1072" />
                                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-1073" />
                                      <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6" data-id="element-1074">
                                        <h4 className="font-heading text-2xl sm:text-3xl font-bold text-white leading-tight mb-1" data-id="element-1075">
                                          {product.name}
                                        </h4>
                                        <p className="text-white/90 text-sm sm:text-base font-medium" data-id="element-1076">
                                          ${product.pricePerYard} / yard
                                        </p>
                                      </div>
                                    </button>)}
                                </div>
                              </div>)}
                          </div>
                          <button onClick={() => {
                      setShowAllCatalog(false);
                      setTimeout(() => {
                        document.getElementById('materials')?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start'
                        });
                      }, 450);
                    }} className="mx-auto flex items-center gap-2 mt-10 py-3 px-6 text-stone-500 dark:text-stone-400 hover:text-brand-orange transition-colors text-sm font-bold uppercase tracking-wider border border-stone-200 dark:border-[#404040] hover:border-brand-orange/50 rounded-lg" data-id="element-1077">
                            <ChevronDown className="w-4 h-4 rotate-180" data-id="element-1078" />
                            Collapse Catalog
                          </button>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </motion.div>}
              {/* CALCULATOR TAB */}
              {activeTab === 'calculator' && <motion.div key="calculator" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="max-w-4xl mx-auto" data-id="element-1079">
                  <div className="bg-[#fdfbf7] dark:bg-[#232221] rounded-2xl border border-stone-200 dark:border-[#333231] shadow-sm overflow-hidden transition-colors duration-300" data-id="element-1080">
                    <div className="grid grid-cols-1 md:grid-cols-5" data-id="element-1081">
                      <div className="p-6 sm:p-8 md:col-span-3 border-b md:border-b-0 md:border-r border-stone-200 dark:border-[#333231]" data-id="element-1082">
                        <form onSubmit={handleCalculate} className="space-y-6" data-id="element-1083">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1084">
                            <div data-id="element-1085">
                              <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1086">
                                Project Type
                              </label>
                              <CustomSelect value={projectType} onChange={e => setProjectType(e)} options={projectTypes.map(p => ({
                            value: p.id,
                            label: p.name
                          }))} placeholder="Select a project type" data-id="element-1087" />
                            </div>
                            <div data-id="element-1088">
                              <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1089">
                                Material
                              </label>
                              <CustomSelect value={material} onChange={e => setMaterial(e)} options={calcMaterials.map(m => ({
                            value: m.id,
                            label: m.name
                          }))} placeholder="Select a material" data-id="element-1090" />
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-4" data-id="element-1091">
                            <div data-id="element-1092">
                              <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1093">
                                Length (ft)
                              </label>
                              <input type="number" value={length} onChange={e => setLength(e.target.value)} placeholder="0" className="w-full px-4 py-3 bg-[#f5f2ee] dark:bg-[#1a1918] border border-stone-300 dark:border-[#2a2928] rounded-xl text-brand-charcoal dark:text-white placeholder-brand-muted dark:placeholder-stone-600 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1094" />
                            </div>
                            <div data-id="element-1095">
                              <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1096">
                                Width (ft)
                              </label>
                              <input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="0" className="w-full px-4 py-3 bg-[#f5f2ee] dark:bg-[#1a1918] border border-stone-300 dark:border-[#2a2928] rounded-xl text-brand-charcoal dark:text-white placeholder-brand-muted dark:placeholder-stone-600 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1097" />
                            </div>
                            <div data-id="element-1098">
                              <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1099">
                                Depth (in)
                              </label>
                              <input type="number" value={depth} onChange={e => setDepth(e.target.value)} className="w-full px-4 py-3 bg-[#f5f2ee] dark:bg-[#1a1918] border border-stone-300 dark:border-[#2a2928] rounded-xl text-brand-charcoal dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1100" />
                            </div>
                          </div>

                          <button type="submit" className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-4 rounded-xl font-medium transition-colors flex items-center justify-center" data-id="element-1101">
                            <Calculator className="w-5 h-5 mr-2" data-id="element-1102" />
                            Calculate Need
                          </button>
                        </form>
                      </div>

                      <div className="p-6 sm:p-8 md:col-span-2 bg-[#f3f0ec] dark:bg-[#1e1d1c] flex flex-col justify-center transition-colors duration-300" data-id="element-1103">
                        {calcResults ? <div className="space-y-6" data-id="element-1104">
                            <div data-id="element-1105">
                              <p className="text-sm text-brand-muted dark:text-stone-400 font-medium mb-1" data-id="element-1106">
                                Estimated Volume
                              </p>
                              <div className="flex items-baseline gap-2" data-id="element-1107">
                                <span className="font-heading text-4xl font-bold text-brand-charcoal dark:text-white" data-id="element-1108">
                                  {calcResults.yards}
                                </span>
                                <span className="text-brand-muted dark:text-stone-400 font-medium" data-id="element-1109">
                                  Cubic Yards
                                </span>
                              </div>
                            </div>
                            <div className="pt-4 border-t border-stone-200 dark:border-[#262626]" data-id="element-1110">
                              <p className="text-sm text-brand-muted dark:text-stone-400 font-medium mb-1" data-id="element-1111">
                                Estimated Weight
                              </p>
                              <div className="flex items-baseline gap-2" data-id="element-1112">
                                <span className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white" data-id="element-1113">
                                  {calcResults.tons}
                                </span>
                                <span className="text-brand-muted dark:text-stone-400 font-medium" data-id="element-1114">
                                  Tons
                                </span>
                              </div>
                            </div>
                            {(() => {
                        const mat = calcMaterials.find(m => m.id === material);
                        const basePrice = mat ? parseInt(mat.priceRange.replace('$', '')) : 0;
                        const yards = calcResults?.yards || 0;
                        const roundedYards = Math.ceil(yards);
                        const minCharge = 5;
                        const smallThreshold = 10;
                        const fullLoad = 12;
                        const billable = Math.max(roundedYards, minCharge);
                        const isBeyondFull = roundedYards > fullLoad;
                        const bulkDiscount = roundedYards >= 24;
                        const calcLoad = (y: number) => {
                          const b = Math.max(y, minCharge);
                          const s = b < smallThreshold ? 1 + (smallThreshold - b) * 0.1 : 1;
                          return b * Math.ceil(basePrice * s);
                        };
                        let total = 0;
                        if (isBeyondFull) {
                          total = calcLoad(fullLoad) + calcLoad(roundedYards - fullLoad);
                        } else {
                          total = calcLoad(roundedYards);
                        }
                        const preDiscount = total;
                        if (bulkDiscount) total = Math.round(total * 0.95);
                        const surcharge = billable < smallThreshold ? 1 + (smallThreshold - billable) * 0.1 : 1;
                        const effectivePrice = Math.ceil(basePrice * surcharge);
                        return <div className="pt-4 border-t border-stone-200 dark:border-[#4b4b4b]" data-id="element-1115">
                                  <p className="text-sm text-stone-500 dark:text-stone-400 font-medium mb-1" data-id="element-1116">
                                    Estimated Cost
                                  </p>
                                  <div className="flex items-baseline gap-2" data-id="element-1117">
                                    <span className="font-heading text-3xl font-bold text-stone-900 dark:text-white" data-id="element-1118">
                                      ${total}
                                    </span>
                                    <span className="text-stone-500 dark:text-stone-400 text-sm font-medium" data-id="element-1119">
                                      + delivery
                                    </span>
                                  </div>
                                  <p className="text-sm text-stone-600 dark:text-white/70 mt-1" data-id="element-1120">
                                    {roundedYards} yds × $
                                    {isBeyondFull ? basePrice : effectivePrice}
                                    /yd
                                  </p>
                                  {roundedYards < minCharge && roundedYards > 0 && <p className="text-xs text-brand-orange mt-1" data-id="element-1121">
                                        {minCharge} yard minimum charge applies
                                      </p>}
                                  {roundedYards >= minCharge && roundedYards < smallThreshold && <p className="text-xs text-brand-orange mt-1" data-id="element-1122">
                                        Small load upcharge · ${basePrice}/yd at{' '}
                                        {smallThreshold}+ yards
                                      </p>}
                                  {isBeyondFull && <p className="text-xs text-brand-orange mt-1" data-id="element-1123">
                                      2 deliveries required ({fullLoad} +{' '}
                                      {roundedYards - fullLoad} yds)
                                    </p>}
                                  {bulkDiscount && <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-semibold" data-id="element-1124">
                                      5% bulk discount applied · was $
                                      {preDiscount}
                                    </p>}
                                  <p className="text-xs text-stone-500 mt-2 flex items-start" data-id="element-1125">
                                    <Info className="w-3 h-3 mr-1 mt-0.5 shrink-0" data-id="element-1126" />
                                    Delivery fee not included. Final price
                                    depends on exact location.
                                  </p>
                                </div>;
                      })()}
                          </div> : <div className="text-center text-brand-muted dark:text-stone-500" data-id="element-1127">
                            <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" data-id="element-1128" />
                            <p data-id="element-1129">
                              Enter your project dimensions to see estimated
                              materials needed.
                            </p>
                          </div>}
                      </div>
                    </div>
                  </div>
                </motion.div>}
              {/* QUIZ TAB */}
              {activeTab === 'quiz' && <motion.div key="quiz" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="max-w-2xl mx-auto" data-id="element-1130">
                  <div className="bg-[#fdfbf7] dark:bg-[#232221] rounded-2xl border border-stone-200 dark:border-[#333231] shadow-sm p-8 text-center transition-colors duration-300" data-id="element-1131">
                    {quizResult ? <motion.div initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} data-id="element-1132">
                        <div className="w-16 h-16 bg-brand-orange/10 dark:bg-brand-orange/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1133">
                          <CheckCircle2 className="w-8 h-8 text-brand-orange" data-id="element-1134" />
                        </div>
                        <h3 className="text-xl text-brand-muted dark:text-stone-200 mb-2" data-id="element-1135">
                          We recommend:
                        </h3>
                        <h2 className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-1136">
                          {quizResult.name}
                        </h2>
                        <p className="text-brand-muted dark:text-stone-200 mb-8 max-w-md mx-auto" data-id="element-1137">
                          {quizResult.desc}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1138">
                          <button onClick={() => setIsQuoteModalOpen(true)} className="bg-brand-button hover:bg-brand-buttonHover text-white px-8 py-3 rounded-xl font-bold transition-colors" data-id="element-1139">
                            Get a Quote
                          </button>
                          <button onClick={resetQuiz} className="flex items-center justify-center px-8 py-3 rounded-xl font-bold text-brand-charcoal dark:text-white bg-brand-lightgray dark:bg-[#3a3a3a] hover:bg-stone-200 dark:hover:bg-[#4b4b4b] transition-colors" data-id="element-1140">
                            <RefreshCw className="w-4 h-4 mr-2" data-id="element-1141" />
                            Start Over
                          </button>
                        </div>
                      </motion.div> : currentQuizQuestion ? <motion.div key={currentQuizQuestion.id} initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} data-id="element-1142">
                        <h3 className="font-heading text-2xl font-bold text-brand-charcoal dark:text-white mb-8" data-id="element-1143">
                          {currentQuizQuestion.question}
                        </h3>
                        <div className="space-y-3" data-id="element-1144">
                          {currentQuizQuestion.options.map(option => <button key={option.value} onClick={() => handleQuizAnswer(currentQuizQuestion.id, option.value, option.result)} className="w-full text-left p-4 rounded-xl border border-stone-200 dark:border-[#333231] bg-white dark:bg-[#1a1918] hover:border-brand-orange dark:hover:border-brand-orange hover:bg-brand-orange/5 dark:hover:bg-brand-orange/10 transition-all group" data-id="element-1145">
                              <div className="flex items-center justify-between" data-id="element-1146">
                                <span className="font-medium text-stone-900 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-1147">
                                  {option.label}
                                </span>
                                <ArrowRight className="w-5 h-5 text-stone-500 dark:text-stone-400 group-hover:text-brand-orange group-hover:translate-x-1 transition-all" data-id="element-1148" />
                              </div>
                            </button>)}
                        </div>
                      </motion.div> : null}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        {/* Can't find what you need? */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="mt-16 flex flex-col md:flex-row items-center justify-between gap-6 bg-transparent border border-stone-200/60 dark:border-[#404040] rounded-2xl px-8 py-6 max-w-7xl mx-auto" data-id="element-1149">
          <div className="text-center md:text-left" data-id="element-1150">
            <p className="text-stone-700 dark:text-stone-300 text-lg font-sub mb-1" data-id="element-1151">
              Can't find what you're looking for?
            </p>
            <p className="text-stone-500 dark:text-stone-400 text-sm" data-id="element-1152">
              We source a wide range of materials beyond what's listed here.
              Tell us what you need and we'll find it for you.
            </p>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-brand-button hover:bg-brand-buttonHover text-white px-8 py-3 rounded-full font-bold transition-colors shrink-0" data-id="element-1153">
            Contact Us
            <ArrowRight className="w-4 h-4" data-id="element-1154" />
          </Link>
        </motion.div>
      </section>

      {/* Calculator Modal */}
      <AnimatePresence data-id="element-1155">
        {showCalcModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1156">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowCalcModal(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-1157" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="relative w-full max-w-4xl bg-white dark:bg-[#3d3d3d] border border-stone-200 dark:border-[#4b4b4b] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]" data-id="element-1158">
              <div className="flex items-center justify-between p-6 border-b border-stone-200 dark:border-[#4b4b4b] shrink-0" data-id="element-1159">
                <h3 className="text-xl font-bold text-stone-900 dark:text-white flex items-center" data-id="element-1160">
                  <Calculator className="w-5 h-5 mr-2 text-brand-orange" data-id="element-1161" />
                  Material Calculator
                </h3>
                <button onClick={() => setShowCalcModal(false)} className="text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors p-1" data-id="element-1162">
                  <X className="w-6 h-6" data-id="element-1163" />
                </button>
              </div>
              <div className="overflow-y-auto" data-id="element-1164">
                <div className="grid grid-cols-1 md:grid-cols-5" data-id="element-1165">
                  <div className="p-6 sm:p-8 md:col-span-3 border-b md:border-b-0 md:border-r border-stone-200 dark:border-[#4b4b4b]" data-id="element-1166">
                    <form onSubmit={handleCalculate} className="space-y-6" data-id="element-1167">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1168">
                        <div data-id="element-1169">
                          <label className="block text-sm font-medium text-stone-600 dark:text-stone-300 mb-2" data-id="element-1170">
                            Project Type
                          </label>
                          <CustomSelect value={projectType} onChange={e => setProjectType(e)} options={projectTypes.map(p => ({
                        value: p.id,
                        label: p.name
                      }))} placeholder="Select a project type" data-id="element-1171" />
                        </div>
                        <div data-id="element-1172">
                          <label className="block text-sm font-medium text-stone-600 dark:text-stone-300 mb-2" data-id="element-1173">
                            Material
                          </label>
                          <CustomSelect value={material} onChange={e => setMaterial(e)} options={calcMaterials.map(m => ({
                        value: m.id,
                        label: m.name
                      }))} placeholder="Select a material" data-id="element-1174" />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4" data-id="element-1175">
                        <div data-id="element-1176">
                          <label className="block text-sm font-medium text-stone-600 dark:text-stone-300 mb-2" data-id="element-1177">
                            Length (ft)
                          </label>
                          <input type="number" value={length} onChange={e => setLength(e.target.value)} placeholder="0" className="w-full px-4 py-3 bg-stone-100 dark:bg-[#303030] border border-stone-300 dark:border-[#4b4b4b] rounded-xl text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1178" />
                        </div>
                        <div data-id="element-1179">
                          <label className="block text-sm font-medium text-stone-600 dark:text-stone-300 mb-2" data-id="element-1180">
                            Width (ft)
                          </label>
                          <input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="0" className="w-full px-4 py-3 bg-stone-100 dark:bg-[#303030] border border-stone-300 dark:border-[#4b4b4b] rounded-xl text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1181" />
                        </div>
                        <div data-id="element-1182">
                          <label className="block text-sm font-medium text-stone-600 dark:text-stone-300 mb-2" data-id="element-1183">
                            Depth (in)
                          </label>
                          <input type="number" value={depth} onChange={e => setDepth(e.target.value)} className="w-full px-4 py-3 bg-stone-100 dark:bg-[#303030] border border-stone-300 dark:border-[#4b4b4b] rounded-xl text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1184" />
                        </div>
                      </div>

                      <button type="submit" className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-4 rounded-xl font-medium transition-colors flex items-center justify-center" data-id="element-1185">
                        <Calculator className="w-5 h-5 mr-2" data-id="element-1186" />
                        Calculate Need
                      </button>
                    </form>
                  </div>

                  <div className="p-6 sm:p-8 md:col-span-2 bg-stone-50 dark:bg-[#303030] flex flex-col justify-center" data-id="element-1187">
                    {calcResults ? <div className="space-y-6" data-id="element-1188">
                        <div data-id="element-1189">
                          <p className="text-sm text-stone-500 dark:text-stone-400 font-medium mb-1" data-id="element-1190">
                            Estimated Volume
                          </p>
                          <div className="flex items-baseline gap-2" data-id="element-1191">
                            <span className="font-heading text-4xl font-bold text-stone-900 dark:text-white" data-id="element-1192">
                              {calcResults.yards}
                            </span>
                            <span className="text-stone-500 dark:text-stone-400 font-medium" data-id="element-1193">
                              Cubic Yards
                            </span>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-stone-200 dark:border-[#4b4b4b]" data-id="element-1194">
                          <p className="text-sm text-stone-500 dark:text-stone-400 font-medium mb-1" data-id="element-1195">
                            Estimated Weight
                          </p>
                          <div className="flex items-baseline gap-2" data-id="element-1196">
                            <span className="font-heading text-3xl font-bold text-stone-900 dark:text-white" data-id="element-1197">
                              {calcResults.tons}
                            </span>
                            <span className="text-stone-500 dark:text-stone-400 font-medium" data-id="element-1198">
                              Tons
                            </span>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-stone-200 dark:border-[#4b4b4b]" data-id="element-1199">
                          <p className="text-sm text-stone-500 dark:text-stone-400 font-medium mb-1" data-id="element-1200">
                            Material Price Range
                          </p>
                          <p className="text-lg font-medium text-stone-900 dark:text-white" data-id="element-1201">
                            {calcMaterials.find(m => m.id === material)?.priceRange}{' '}
                            <span className="text-sm text-stone-500 font-normal" data-id="element-1202">
                              / yard
                            </span>
                          </p>
                          <p className="text-xs text-stone-500 mt-1 flex items-start" data-id="element-1203">
                            <Info className="w-3 h-3 mr-1 mt-0.5 shrink-0" data-id="element-1204" />
                            Delivery fee not included. Final price depends on
                            exact location.
                          </p>
                        </div>
                      </div> : <div className="text-center text-stone-400 dark:text-stone-500" data-id="element-1205">
                        <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" data-id="element-1206" />
                        <p data-id="element-1207">
                          Enter your project dimensions to see estimated
                          materials needed.
                        </p>
                      </div>}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Quiz Modal */}
      <AnimatePresence data-id="element-1208">
        {showQuizModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1209">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowQuizModal(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-1210" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="relative w-full max-w-2xl bg-white dark:bg-[#3d3d3d] border border-stone-200 dark:border-[#4b4b4b] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]" data-id="element-1211">
              <div className="flex items-center justify-between p-6 border-b border-stone-200 dark:border-[#4b4b4b] shrink-0" data-id="element-1212">
                <h3 className="text-xl font-bold text-stone-900 dark:text-white flex items-center" data-id="element-1213">
                  <ArrowRight className="w-5 h-5 mr-2 text-brand-orange" data-id="element-1214" />
                  Help Me Choose
                </h3>
                <button onClick={() => setShowQuizModal(false)} className="text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors p-1" data-id="element-1215">
                  <X className="w-6 h-6" data-id="element-1216" />
                </button>
              </div>
              <div className="overflow-y-auto p-8 text-center" data-id="element-1217">
                {quizResult ? <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} data-id="element-1218">
                    <div className="w-16 h-16 bg-brand-orange/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1219">
                      <CheckCircle2 className="w-8 h-8 text-brand-orange" data-id="element-1220" />
                    </div>
                    <h3 className="text-xl text-stone-500 dark:text-stone-400 mb-2" data-id="element-1221">
                      We recommend:
                    </h3>
                    <h2 className="font-heading text-3xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-1222">
                      {quizResult.name}
                    </h2>
                    <p className="text-stone-600 dark:text-stone-400 mb-8 max-w-md mx-auto" data-id="element-1223">
                      {quizResult.desc}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1224">
                      <button onClick={() => {
                  setShowQuizModal(false);
                  setIsQuoteModalOpen(true);
                }} className="bg-brand-button hover:bg-brand-buttonHover text-white px-8 py-3 rounded-xl font-bold transition-colors" data-id="element-1225">
                        Get a Quote
                      </button>
                      <button onClick={resetQuiz} className="flex items-center justify-center px-8 py-3 rounded-xl font-bold text-stone-900 dark:text-white bg-stone-100 dark:bg-[#3a3a3a] hover:bg-stone-200 dark:hover:bg-[#4b4b4b] transition-colors" data-id="element-1226">
                        <RefreshCw className="w-4 h-4 mr-2" data-id="element-1227" />
                        Start Over
                      </button>
                    </div>
                  </motion.div> : currentQuizQuestion ? <motion.div key={currentQuizQuestion.id} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} data-id="element-1228">
                    <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-8" data-id="element-1229">
                      {currentQuizQuestion.question}
                    </h3>
                    <div className="space-y-3" data-id="element-1230">
                      {currentQuizQuestion.options.map(option => <button key={option.value} onClick={() => handleQuizAnswer(currentQuizQuestion.id, option.value, option.result)} className="w-full text-left p-4 rounded-xl border border-stone-200 dark:border-[#4b4b4b] bg-stone-50 dark:bg-[#303030] hover:border-brand-orange hover:bg-brand-orange/10 transition-all group" data-id="element-1231">
                          <div className="flex items-center justify-between" data-id="element-1232">
                            <span className="font-medium text-stone-900 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-1233">
                              {option.label}
                            </span>
                            <ArrowRight className="w-5 h-5 text-stone-300 dark:text-stone-700 group-hover:text-brand-orange group-hover:translate-x-1 transition-all" data-id="element-1234" />
                          </div>
                        </button>)}
                    </div>
                  </motion.div> : null}
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-1235" />

      {/* Cart Added Toast removed — inline pill handles this now */}

      {/* Swipe Navigation Hint */}
      <AnimatePresence data-id="element-1236">
        {showSwipeHint && <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 30
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 25
      }} onClick={() => {
        setShowSwipeHint(false);
        setSwipeHintDismissed(true);
      }} className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-brand-charcoal/95 dark:bg-[#2a2a2a]/95 text-white px-5 py-3 rounded-xl shadow-2xl border border-white/10 flex items-center gap-3 backdrop-blur-md cursor-pointer md:hidden" data-id="element-1237">
            <span className="text-2xl" data-id="element-1238">👆</span>
            <div data-id="element-1239">
              <p className="text-sm font-bold" data-id="element-1240">Swipe to browse products</p>
              <p className="text-xs text-white/60" data-id="element-1241">
                Swipe left or right to see the next material
              </p>
            </div>
            <X className="w-4 h-4 text-white/40 shrink-0 ml-2" data-id="element-1242" />
          </motion.div>}
      </AnimatePresence>
    </>;
}